import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { 
  ArrowRightIcon, 
  CloudIcon, 
  ServerIcon, 
  ShieldCheckIcon,
  CpuChipIcon,
  CircleStackIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <Logo size="sm" />
            
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Dashboard
                </Link>
              ) : (
                <div className="flex space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center py-16">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-6xl">
            Optimize Your AWS Spend with
            <span className="text-indigo-600 dark:text-indigo-400 whitespace-nowrap"> On-Premise Migration</span>
          </h1>
          <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Analyze your AWS infrastructure, calculate costs, and discover potential savings by migrating to on-premise solutions. Get detailed insights into your compute, storage, and networking resources.
          </p>
          <div className="mt-10">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                View Dashboard
                <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5" />
              </Link>
            ) : (
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Start Free Analysis
                <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5" />
              </Link>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            Comprehensive AWS Resource Analysis
          </h2>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={<CpuChipIcon className="h-8 w-8" />}
              title="Compute Resources"
              description="Track EC2 instances, vCPUs, memory usage, and compute costs across all regions."
            />
            <FeatureCard
              icon={<CircleStackIcon className="h-8 w-8" />}
              title="Storage Analysis"
              description="Monitor EBS volumes, S3 buckets, and storage costs with detailed breakdowns."
            />
            <FeatureCard
              icon={<CloudIcon className="h-8 w-8" />}
              title="EKS & Kubernetes"
              description="Analyze EKS clusters, node groups, and containerized workload requirements."
            />
            <FeatureCard
              icon={<CurrencyDollarIcon className="h-8 w-8" />}
              title="Cost Optimization"
              description="Calculate potential savings and ROI for on-premise migration scenarios."
            />
            <FeatureCard
              icon={<ServerIcon className="h-8 w-8" />}
              title="Hardware Planning"
              description="Get recommendations for physical servers, RAM, and storage requirements."
            />
            <FeatureCard
              icon={<ShieldCheckIcon className="h-8 w-8" />}
              title="Secure & Private"
              description="Your AWS credentials are encrypted and your data remains completely private."
            />
          </div>
        </div>

        {/* Migration Benefits */}
        <div className="mt-20 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
              Why Consider On-Premise Migration?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">40-60%</div>
                <div className="text-lg font-medium text-gray-900 dark:text-white">Cost Savings</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Typical savings after 3-year migration
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">100%</div>
                <div className="text-lg font-medium text-gray-900 dark:text-white">Data Control</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Complete control over your infrastructure
                </div>
              </div>
              <div>
                <div className="text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">24/7</div>
                <div className="text-lg font-medium text-gray-900 dark:text-white">Predictable Costs</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  No surprise bills or usage spikes
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Connect Your AWS Account
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Securely connect your AWS account with read-only access to analyze your current infrastructure.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Analyze Resources
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Our platform scans your AWS resources and calculates current costs and usage patterns.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Get Migration Plan
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Receive detailed recommendations for hardware requirements and potential cost savings.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Start Saving?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Get a free analysis of your AWS infrastructure in minutes.
          </p>
          {!isAuthenticated && (
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Create Free Account
              <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="text-indigo-600 dark:text-indigo-400">{icon}</div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
      <p className="mt-2 text-gray-600 dark:text-gray-300">{description}</p>
    </div>
  );
};

export default HomePage;